import React, { useState, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import ResultsTile from './ResultsTile';
import FieldModal from './FieldModal';
import { createOrUpdateOverride, getOverridesForLoan } from '../store/slices/overridesSlice';
import { useFilter } from '../contexts/FilterContext';
import { Check, AlertTriangle, X, Circle, Search } from 'lucide-react';
import { getMatchStatusFromRow } from '../services/colorService';
import { rerunFieldExtraction } from '../services/fieldExtractionService';
import socketService from '../services/socketService';
import './ResultsTile.css';

const ResultsGrid = ({ results, loanId, actionButtons }) => {
  const dispatch = useDispatch();
  const { searchQuery, activeFilter } = useFilter();
  
  const { overrides, loading } = useSelector(state => state.overrides);
  const extractionResults = useSelector(state => state.documents.extractionResults);
  const [fieldModal, setFieldModal] = useState({ isOpen: false, fieldName: null, currentValues: null, loanId: null, borrowerName: null });
  
  // Check if editing should be disabled (data not saved to database yet)
  // Disable editing if we're in preview mode (saved_to_database === false or preview_mode === true)
  // If extractionResults doesn't exist or flags are undefined, assume it's saved data (allow editing)
  const isEditingDisabled = extractionResults && (
    extractionResults.saved_to_database === false || 
    extractionResults.preview_mode === true
  );
  
  // Load overrides when loanId changes
  React.useEffect(() => {
    if (loanId && loanId !== 'Unknown') {
      dispatch(getOverridesForLoan({ loanId, source: 'ResultsGrid.jsx:22' }));
    }
  }, [loanId, dispatch]);

  // Apply overrides to results
  const applyOverridesToResults = useMemo(() => {
    const overriddenResults = { ...(results || {}) };
    
    overrides.forEach(override => {
      const fieldName = override.field_name;
      if (overriddenResults[fieldName]) {
        switch (override.override_type) {
          case 'value':
            overriddenResults[fieldName] = {
              ...overriddenResults[fieldName],
              value: override.overridden_value,
              overridden: true,
              override_comment: override.comment,
              override_created_by: override.created_by
            };
            break;
          case 'master_value':
            overriddenResults[fieldName] = {
              ...overriddenResults[fieldName],
              master_value: override.overridden_value,
              overridden: true,
              override_comment: override.comment,
              override_created_by: override.created_by
            };
            break;
          case 'source':
            overriddenResults[fieldName] = {
              ...overriddenResults[fieldName],
              source: override.overridden_value,
              overridden: true,
              override_comment: override.comment,
              override_created_by: override.created_by
            };
            break;
          case 'match_status':
            overriddenResults[fieldName] = {
              ...overriddenResults[fieldName],
              match_status_override: override.overridden_value,
              overridden: true,
              override_comment: override.comment,
              override_created_by: override.created_by
            };
            break;
          case 'flag':
            overriddenResults[fieldName] = {
              ...overriddenResults[fieldName],
              flag_override: override.overridden_value,
              overridden: true,
              override_comment: override.comment,
              override_created_by: override.created_by
            };
            break;
        }
      }
    });
    
    return overriddenResults;
  }, [results, overrides]);

  // Filter out metadata from the results and use overridden results
  const fieldResults = Object.entries(applyOverridesToResults).filter(([key]) => key !== '_metadata');

  const handleEditField = (fieldName, data) => {
    
    // Try to get borrower name from the "Borrower Name" field if it exists in the results
    let borrowerName = metadata?.borrower_name || metadata?.borrower || data.borrower_name || data.borrower;
    
    // If not found in metadata, try to get it from the "Borrower Name" field in the results
    if (!borrowerName && applyOverridesToResults['Borrower Name']) {
      const borrowerData = applyOverridesToResults['Borrower Name'];
      borrowerName = borrowerData.value || borrowerData.master_value;
    }
    
    // Final fallback to loan ID
    if (!borrowerName) {
      borrowerName = loanId;
    }


    const currentValues = {
      value: data?.value || 'Not Found',
      master_value: data?.master_value || 'Not Found',
      source: data?.source || 'N/A',
      match_status_override: data?.match_status_override,
      flag_override: data?.flag_override,
      system_document_match: data?.system_document_match
    };
    
    setFieldModal({
      isOpen: true,
      fieldName,
      fieldUuid: data?.field_uuid,  // Pass UUID for API calls
      currentValues,
      loanId: loanId,
      borrowerName: borrowerName
    });
  };

  const handleSaveOverride = async (overrideData) => {
    try {
      for (const overrideType of overrideData.overrideTypes) {
        await dispatch(createOrUpdateOverride({
          loanId,
          fieldName: overrideData.fieldName,
          overrideType,
          overriddenValue: overrideData.overrideValues[overrideType],
          overriddenKey: null,
          comment: overrideData.comment,
          createdBy: overrideData.createdBy
        })).unwrap();
      }
      // Refresh overrides after saving
      dispatch(getOverridesForLoan({ loanId, source: 'ResultsGrid.jsx:112' }));
    } catch (error) {
      throw new Error(error.message || 'Failed to save override');
    }
  };

  const handleRerunFieldExtraction = async (loanId, fieldName) => {
    try {
      console.log(`\n${'='.repeat(80)}`);
      console.log(`🔄 ResultsGrid: Starting field extraction for ${fieldName} in loan ${loanId}`);
      
      // Socket.IO handles connection management automatically
      
      // Log current Socket.IO state
      console.log(`🔌 [ResultsGrid] Socket.IO state: ${socketService.isReady() ? 'CONNECTED' : 'NOT CONNECTED'}`);
      
      // Wait for Socket.IO connection to be ready
      try {
        console.log(`⏳ [ResultsGrid] Waiting for Socket.IO connection...`);
        await socketService.waitForConnection();
        
        // Log state AFTER connection
        const readyStateAfter = socketService.isReady() ? 'CONNECTED' : 'NOT CONNECTED';
        console.log(`🔌 [ResultsGrid] Socket.IO state AFTER waitForConnection: ${readyStateAfter}`);
        console.log('✅ [ResultsGrid] Socket.IO connection ready');
      } catch (error) {
        console.warn('⚠️ [ResultsGrid] Socket.IO connection timeout:', error);
      }
      
      // Always use real API - no more mock service in development
      console.log(`📞 [ResultsGrid] Calling API`);
      const result = await rerunFieldExtraction(loanId, fieldName);
      
      console.log(`✅ ResultsGrid: Field extraction completed for ${fieldName}:`, result);
      console.log(`${'='.repeat(80)}\n`);
      return result;
      
    } catch (error) {
      console.error(`❌ ResultsGrid: Field extraction failed for ${fieldName}:`, error);
      console.log(`${'='.repeat(80)}\n`);
      throw error;
    }
  };
  
  
  // Get metadata if available
  const metadata = results?._metadata || {};

  // Filter and search logic
  const filteredResults = useMemo(() => {
    let filtered = fieldResults;
    
    // Apply search filter
    if (searchQuery && searchQuery.trim()) {
      const searchLower = searchQuery.toLowerCase();
      filtered = filtered.filter(([fieldName, data]) => {
        return (
          fieldName.toLowerCase().includes(searchLower) ||
          data.value?.toLowerCase().includes(searchLower) ||
          data.master_value?.toLowerCase().includes(searchLower) ||
          data.source?.toLowerCase().includes(searchLower) ||
          data.comment?.toLowerCase().includes(searchLower) ||
          data.document_date?.toLowerCase().includes(searchLower) ||
          data.page?.toString().includes(searchLower) ||
          data.document_match?.toLowerCase().includes(searchLower) ||
          data.system_document_match?.toLowerCase().includes(searchLower)
        );
      });
    }

    // Apply category filter
    if (activeFilter !== 'all') {
      filtered = filtered.filter(([, data]) => {
        if (!data) return false; // Skip null/undefined data
        
        switch (activeFilter) {
          case 'perfect':
            const perfectStatus = data.system_document_match || data.match_status_override || 'NO_MATCH';
            return perfectStatus === 'HIGH' || perfectStatus === 'PERFECT_MATCH';
          case 'maybe':
            const maybeStatus = data.system_document_match || data.match_status_override || 'NO_MATCH';
            return maybeStatus === 'MEDIUM' || maybeStatus === 'LOW' || maybeStatus === 'MAYBE_MATCH';
          case 'mismatch':
            const mismatchStatus = data.system_document_match || data.match_status_override || 'NO_MATCH';
            return mismatchStatus === 'NO_MATCH';
          case 'hasData':
            return data.value && data.value !== 'Not Found' && data.value.trim() !== '';
          case 'hasCoreData':
            return data.master_value && data.master_value !== 'Not Found' && data.master_value.trim() !== '';
          case 'noCoreData':
            return !data.master_value || data.master_value === 'Not Found' || data.master_value.trim() === '';
          case 'clientReview':
            return data.flag_override === 'client_review';
          case 'leadReview':
            return data.flag_override === 'lead_review';
          case 'overridden':
            return data.overridden === true;
          default:
            return true;
        }
      });
    }

    return filtered;
  }, [fieldResults, searchQuery, activeFilter]);

  return (
    <div className="results-container" data-cy="results-container">
      {/* Header with Action Buttons */}
      {actionButtons && (
        <div className="results-header">
          <div style={{ display: 'flex', gap: '0.75rem', alignItems: 'center' }}>
            {actionButtons}
          </div>
        </div>
      )}




      {/* Results Grid */}
      <div className="results-grid" data-cy="results-grid">
        {filteredResults.length > 0 ? (
          filteredResults.map(([fieldName, data]) => (
            <ResultsTile 
              key={fieldName} 
              fieldName={fieldName} 
              data={data}
              onEdit={isEditingDisabled ? null : handleEditField}
              editingDisabled={isEditingDisabled}
              data-cy="result-tile"
            />
          ))
        ) : (
          <div className="no-results">
            <div className="no-results-icon">
              <Search size={48} color="#9ca3af" />
            </div>
            <h3>No results found</h3>
            <p>
              {fieldResults.length === 0 
                ? "No fields have been extracted yet. Upload and process documents to see results."
                : (searchQuery && searchQuery.trim() 
                    ? `No fields match "${searchQuery}". Try adjusting your search or filter.`
                    : activeFilter !== 'all'
                      ? `No fields match the "${activeFilter}" filter. Try selecting a different filter.`
                      : "No results match the current criteria."
                  )
              }
            </p>
          </div>
        )}
      </div>

      {/* Legend */}
      <div className="results-legend">
        <h4>Match Status Legend</h4>
        <div className="legend-items">
          <div className="legend-item">
            <div className="match-indicator status-icon" style={{ 
              background: '#dcfce7', 
              border: '2px solid #22c55e',
              color: '#16a34a'
            }}>
              <Check size={10} />
            </div>
            <span>Perfect Match</span>
          </div>
          <div className="legend-item">
            <div className="match-indicator status-icon" style={{ 
              background: '#fef3c7', 
              border: '2px solid #f59e0b',
              color: '#d97706'
            }}>
              <AlertTriangle size={10} />
            </div>
            <span>Maybe Match</span>
          </div>
          <div className="legend-item">
            <div className="match-indicator status-icon" style={{ 
              background: '#fef2f2', 
              border: '2px solid #ef4444',
              color: '#dc2626'
            }}>
              <X size={10} />
            </div>
            <span>Mismatch</span>
          </div>
          <div className="legend-item">
            <div className="match-indicator status-icon" style={{ 
              background: '#f3f4f6', 
              border: '2px solid #9ca3af',
              color: '#6b7280'
            }}>
              <Circle size={10} />
            </div>
            <span>No Data</span>
          </div>
        </div>
      </div>

      {/* Field Modal */}
      <FieldModal
        isOpen={fieldModal.isOpen}
        onClose={() => setFieldModal({ isOpen: false, fieldName: null, fieldUuid: null, currentValues: null, loanId: null, borrowerName: null })}
        fieldName={fieldModal.fieldName}
        fieldUuid={fieldModal.fieldUuid}
        currentValues={fieldModal.currentValues}
        loanId={fieldModal.loanId}
        borrowerName={fieldModal.borrowerName}
        onSave={handleSaveOverride}
        loading={loading.create}
        showDocumentResults={false}
        historyData={[]}
        onRerunFieldExtraction={handleRerunFieldExtraction}
      />
    </div>
  );
};

export default ResultsGrid;
